import drawingAlerts from '../../server/alerts/drawing.cjs';
// Pure chart-workspace calculations. No broker calls or account credentials.
export const SCHEMA_VERSION = 1;
export const clone = (value) => JSON.parse(JSON.stringify(value));
export const isDrawing = (overlay) =>
    Boolean(overlay.settings && overlay.settings.$state);
export const marketKey = ({ connector, exchange, symbol }) =>
    [connector, exchange, symbol].join(':');

export function candlesToCSV(candles) {
    return [
        'Time (UTC),Open,High,Low,Close,Volume',
        ...candles.map((row) =>
            [new Date(row[0]).toISOString(), ...row.slice(1, 7)].join(',')
        ),
    ].join('\n');
}

export function replayData(data, count) {
    const copy = clone(data);
    copy.chart.data = copy.chart.data.slice(
        0,
        Math.max(1, Math.min(count, copy.chart.data.length))
    );
    const cutoff = copy.chart.data[copy.chart.data.length - 1]?.[0];
    for (const side of ['onchart', 'offchart']) {
        // Retained outputs are clipped at the visible bar. Confirmed pivots may
        // still repaint: this is historical viewing, not a strategy backtest.
        copy[side] = (copy[side] || [])
            .filter((overlay) => !isDrawing(overlay))
            .map((overlay) => ({
                ...overlay,
                data: overlay.settings?.$workspaceComparison
                    ? []
                    : (overlay.data || []).filter(
                          (row) => Array.isArray(row) && row[0] <= cutoff
                      ),
            }));
    }
    return copy;
}

export function normalizedComparison(primary, other) {
    const byTime = new Map(other.map((row) => [row[0], row[4]]));
    const base = primary.find((row) => row[4] > 0 && byTime.get(row[0]) > 0);
    if (!base)
        throw new Error('The series have no common bars with positive prices.');
    const ratio = base[4] / byTime.get(base[0]);
    return primary
        .filter(
            (row) => row[0] >= base[0] && Number.isFinite(byTime.get(row[0]))
        )
        .map((row) => [row[0], byTime.get(row[0]) * ratio]);
}

export function priceCondition(condition, previous, price, target) {
    if (!Number.isFinite(price) || !Number.isFinite(target)) return false;
    switch (condition) {
        case 'above':
            return price > target;
        case 'below':
            return price < target;
        case 'crossing_up':
            return (
                Number.isFinite(previous) &&
                previous <= target &&
                price > target
            );
        case 'crossing_down':
            return (
                Number.isFinite(previous) &&
                previous >= target &&
                price < target
            );
        case 'crossing':
            return (
                Number.isFinite(previous) &&
                ((previous <= target && price > target) ||
                    (previous >= target && price < target))
            );
        default:
            return false;
    }
}

export function evaluateAlerts(alerts, key, previous, price, now) {
    const fired = [];
    const next = alerts.map((alert) => {
        if (
            alert.key !== key ||
            !alert.enabled ||
            (alert.expiresAt && alert.expiresAt < now)
        )
            return alert;
        const matches=alert.drawing?drawingAlerts.drawingCondition(alert.condition,alert.drawing,alert._observed,{price,time:now}):priceCondition(alert.condition,previous,price,alert.target);
        if(!matches)return alert.drawing?{...alert,_observed:{price,time:now}}:alert;
        const result = {
            ...alert,
            enabled: false,
            triggeredAt: now,
            triggeredPrice: price,
        };
        fired.push(result);
        return result;
    });
    return { alerts: next, fired };
}

export function newPaperAccount(balance = 100000) {
    return {
        cash: balance,
        initialBalance: balance,
        realized: 0,
        positions: {},
        orders: [],
        fills: [],
    };
}

export function paperEquity(account, marks = {}) {
    return (
        account.cash +
        Object.entries(account.positions).reduce(
            (sum, [key, position]) =>
                sum +
                position.quantity *
                    (marks[key] || position.mark || position.average),
            0
        )
    );
}

export function fillPaperOrder(account, order, price, now) {
    if (
        !Number.isFinite(price) ||
        price <= 0 ||
        !Number.isFinite(order.quantity) ||
        order.quantity <= 0 ||
        !['buy', 'sell'].includes(order.side)
    )
        throw new Error('Enter a positive quantity and a valid price.');
    const next = clone(account);
    const current = next.positions[order.key] || {
        quantity: 0,
        average: price,
        mark: price,
    };
    const signed = (order.side === 'buy' ? 1 : -1) * order.quantity;
    const quantity = current.quantity + signed;
    // Cash-account simulation: no leverage or exchange/broker execution.
    const otherExposure = Object.entries(next.positions).reduce(
        (sum, [key, position]) =>
            sum +
            (key === order.key
                ? 0
                : Math.abs(position.quantity * position.mark)),
        0
    );
    if (
        Math.abs(quantity * price) + otherExposure >
        paperEquity(next, { [order.key]: price }) + 1e-8
    )
        throw new Error('Insufficient simulated buying power.');
    let average = current.average;
    if (
        !current.quantity ||
        Math.sign(current.quantity) === Math.sign(signed)
    ) {
        average =
            (Math.abs(current.quantity) * current.average +
                Math.abs(signed) * price) /
            Math.abs(quantity);
    } else {
        next.realized +=
            Math.min(Math.abs(signed), Math.abs(current.quantity)) *
            (price - current.average) *
            Math.sign(current.quantity);
        if (quantity && Math.sign(quantity) !== Math.sign(current.quantity))
            average = price;
    }
    next.cash -= signed * price;
    if (Math.abs(quantity) < 1e-10) delete next.positions[order.key];
    else
        next.positions[order.key] = {
            quantity,
            average,
            mark: price,
            symbol: order.symbol,
        };
    next.fills.unshift({ ...order, price, time: now });
    next.fills = next.fills.slice(0, 200);
    return next;
}

export function processPaperOrders(account, key, price, now) {
    let next = clone(account);
    if (next.positions[key]) next.positions[key].mark = price;
    for (const order of [...next.orders]) {
        if (order.key !== key) continue;
        const hit =
            order.type === 'limit'
                ? order.side === 'buy'
                    ? price <= order.price
                    : price >= order.price
                : order.side === 'buy'
                ? price >= order.price
                : price <= order.price;
        if (!hit) continue;
        next.orders = next.orders.filter((item) => item.id !== order.id);
        try {
            next = fillPaperOrder(next, order, price, now);
        } catch (error) {
            next.fills.unshift({
                ...order,
                rejected: error.message,
                time: now,
            });
        }
    }
    return next;
}

export function workspaceSnapshot(data, context) {
    const snapshot = {
        version: SCHEMA_VERSION,
        ...context,
        savedAt: Date.now(),
        drawingPrefs: clone(data.drawingPrefs || {}),
        chartSettings: clone(data.chart.settings || {}),
        chartGrid: clone(data.chart.grid || {}),
        onchart: clone(data.onchart || []),
        offchart: clone(data.offchart || []),
    };
    // Market histories are fetched from providers, never stored with workspaces.
    for (const overlay of [...snapshot.onchart, ...snapshot.offchart]) {
        if (!isDrawing(overlay)) overlay.data = [];
        delete overlay.id;
        if (overlay.settings) delete overlay.settings.$selected;
    }
    return snapshot;
}

export function validateWorkspace(value) {
    if (
        !value ||
        value.version !== SCHEMA_VERSION ||
        typeof value.name !== 'string' ||
        !Array.isArray(value.onchart) ||
        !Array.isArray(value.offchart) ||
        typeof value.symbol !== 'string' ||
        typeof value.timeframe !== 'string' ||
        value.name.length > 100 ||
        value.onchart.length + value.offchart.length > 200
    )
        throw new Error('This is not a supported TradingCandle workspace.');
    const serialized = JSON.stringify(value);
    if (serialized.length > 2000000)
        throw new Error('Workspace file exceeds 2 MB.');
    for (const overlay of [...value.onchart, ...value.offchart]) {
        if (
            !overlay ||
            typeof overlay.type !== 'string' ||
            !Array.isArray(overlay.data) ||
            !overlay.settings ||
            typeof overlay.settings !== 'object'
        )
            throw new Error('The workspace contains an invalid chart object.');
    }
    return clone(value);
}
